import {
    Injectable, inject
} from "@angular/core";
import {
    ModelAction, ISearchSort, SearchSortDirection
} from "@shopliftr/common-js/data";
import {
    BulkResponse,
    BulkUtil,
    GetResponse, RequestUtil, SearchRequest, SearchResponse
} from "@shopliftr/common-js/shared";
import {
    ByodChain, ByodChainSearchFilterOptions
} from "@shopliftr/common-js/smart";
import {
    IReadOnlyInternalApiClientService, InternalApiRequestService,
    IQueryInternalApiClientService,
    ListUtil
} from "@shopliftr/common-ng";
import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

/**
 * HTTP Request Service for ByodChain entities.
 *
 * Implements the **Repository Pattern** (API Client Service), providing a clean abstraction
 * for CRUD operations and search functionality. Delegates HTTP communication to
 * InternalApiRequestService while encapsulating ByodChain-specific request/response handling.
 *
 * @pattern Repository Pattern / HTTP Request Service
 * @providedIn root
 * @implements {IReadOnlyInternalApiClientService<ByodChain>}
 * @implements {IQueryInternalApiClientService<ByodChain>}
 */
@Injectable({ providedIn: "root" })
export class ByodChainApiClientService implements IReadOnlyInternalApiClientService<ByodChain>,
IQueryInternalApiClientService<ByodChain> {
    /**
     * Base API path segment for constructing ByodChain endpoint URLs.
     */
    readonly basePath = "byod-chain";

    /**
     * Handles HTTP communication with Internal API including request validation,
     * URL generation, and centralized error handling.
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves a single ByodChain entity by its unique identifier.
     *
     * @param id - The unique identifier of the ByodChain to retrieve
     * @returns Observable that emits the requested ByodChain entity
     * @example
     * this.byodChainService.get('chain-123').subscribe(chain => {
     *   console.log(chain.name);
     * });
     */
    get$(id: string): Observable<ByodChain> {

        const request = RequestUtil.createGetRequest<ByodChain>(id, ByodChain.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$(request, this.basePath).pipe(map((response: GetResponse<ByodChain>) => {

            return response.payload;
        }));
    }

    /**
     * Retrieves all ByodChain entities matching the specified filter criteria.
     * Results are returned as a complete array without pagination.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [sort] - Optional sorting configuration. Defaults to ascending sort by name
     * @returns Observable that emits an array of all matching ByodChain entities
     * @example
     * this.byodChainService.getAll({ active: true }).subscribe(chains => {
     *   console.log(`Found ${chains.length} active chains`);
     * });
     */
    getAll$(filterOptions: ByodChainSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<ByodChain>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, ByodChain.modelName, undefined, undefined, filterOptions, sort ?? [{
                field: "name",
                direction: SearchSortDirection.Ascending
            }]
        );

        return this._internalApiRequestService.getAll$<ByodChain, ByodChainSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Performs a paginated search for ByodChain entities matching the specified criteria.
     * Returns results with pagination metadata for efficient data loading.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [page] - Zero-based page number for pagination
     * @param [pageSize] - Number of results per page
     * @param [sort] - Optional sorting configuration
     * @returns Observable that emits a SearchResponse containing matching entities and pagination metadata
     */
    search$(
        filterOptions: ByodChainSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ByodChain>> {

        const request: SearchRequest<ByodChainSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ByodChain.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$<ByodChain, ByodChainSearchFilterOptions>(request, this.basePath);
    }

    /** @deprecated Use search$ instead */
    searchCampaign$(
        filterOptions: ByodChainSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ByodChain>> {
        return this.search$(filterOptions, page, pageSize, sort);
    }

    /**
     * Retrieves multiple ByodChain entities in a single bulk request.
     * More efficient than making individual get() calls when fetching multiple entities.
     *
     * @param ids - Array of unique identifiers for the ByodChains to retrieve
     * @returns Observable that emits a BulkResponse containing all requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ByodChain>(id, ByodChain.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /** @deprecated Use bulkGet$ instead */
    getByIds$(ids: Array<string>): Observable<BulkResponse> {
        return this.bulkGet$(ids);
    }
}
